import React from "react";
import { Button } from "./Button";
import {
  AppError,
  getErrorColors,
  getErrorConfig,
} from "../../utils/errorHandling";

// Extract Button variant type
type ButtonVariant = React.ComponentProps<typeof Button>["variant"];

interface ErrorDisplayProps {
  error: AppError;
  onRetry?: () => void;
  onLogin?: () => void;
  customActions?: Array<{
    label: string;
    action: () => void;
    variant?: ButtonVariant;
  }>;
  className?: string;
  showIcon?: boolean;
  compact?: boolean;
}

const ErrorDisplay: React.FC<ErrorDisplayProps> = ({
  error,
  onRetry,
  onLogin,
  customActions = [],
  className = "",
  showIcon = true,
  compact = false,
}) => {
  const colors = getErrorColors(error.type);
  const config = getErrorConfig(error.type);

  const handleLogin = () => {
    if (onLogin) {
      onLogin();
    } else {
      window.location.href = "/login";
    }
  };

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    }
  };

  const renderIcon = () => {
    if (!showIcon) return null;

    const iconMap = {
      network: (
        <svg
          className={`h-8 w-8 ${colors.icon}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0"
          />
        </svg>
      ),
      auth: (
        <svg
          className={`h-8 w-8 ${colors.icon}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
          />
        </svg>
      ),
      server: (
        <svg
          className={`h-8 w-8 ${colors.icon}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01"
          />
        </svg>
      ),
      validation: (
        <svg
          className={`h-8 w-8 ${colors.icon}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
          />
        </svg>
      ),
      unknown: (
        <svg
          className={`h-8 w-8 ${colors.icon}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    };

    return iconMap[error.type] || iconMap.unknown;
  };

  if (compact) {
    return (
      <div
        className={`flex items-center gap-2 p-3 rounded-lg ${colors.background} ${className}`}
      >
        {showIcon && <div className="flex-shrink-0">{renderIcon()}</div>}
        <span className={`text-sm font-medium ${colors.text}`}>
          {error.message}
        </span>
        <div className="flex gap-2 ml-auto">
          {config.showRetry && onRetry && (
            <Button
              variant="neutral-tertiary"
              size="small"
              onClick={handleRetry}
            >
              Retry
            </Button>
          )}
          {config.showLogin && (
            <Button
              variant="neutral-tertiary"
              size="small"
              onClick={handleLogin}
            >
              Login
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex w-full items-center justify-center py-12 ${className}`}
    >
      <div className="flex flex-col items-center gap-4">
        <div
          className={`flex h-16 w-16 items-center justify-center rounded-full ${colors.background}`}
        >
          {renderIcon()}
        </div>
        <div className="flex flex-col items-center gap-2">
          <span className={`text-body font-body text-center ${colors.text}`}>
            {error.message}
          </span>
          <div className="flex gap-2">
            {config.showRetry && onRetry && (
              <Button
                variant="neutral-tertiary"
                size="small"
                onClick={handleRetry}
              >
                Retry
              </Button>
            )}
            {config.showLogin && (
              <Button
                variant="neutral-tertiary"
                size="small"
                onClick={handleLogin}
              >
                Go to Login
              </Button>
            )}
            {customActions.map((action, index) => (
              <Button
                key={index}
                variant={action.variant || "neutral-tertiary"}
                size="small"
                onClick={action.action}
              >
                {action.label}
              </Button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ErrorDisplay;
